import type {ReactElement} from 'react';
import type {Component} from 'grapesjs';
import {clearComponentStyleProperty, setComponentStyleProperty} from '@FormMakerReact/lib/form-maker-style-library';
import {useComponentStyleProperty} from '@FormMakerReact/lib/form-maker-style-hooks';
import {Text, TextSize} from '@Components/text/text';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {ColorPickerPopup} from '@Components/color-picker-popup';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {colorToHexString} from '@Utils/color.util';
import type {ClearButtonRenderArgs, ColorControlProperty, ColorControlProps} from './color-control.types';
import controlStyles from '@FormMakerReact/layout/form-maker-right-panel/controls/control-row.module.scss';
import styles from './color-control.module.scss';

export function ColorControl({component, property, labelKey, pickAriaKey, clearAriaKey, defaultColor, hideClearButton}: ColorControlProps): ReactElement {
  const rawColor = useComponentStyleProperty(component, property);
  const isUnset = rawColor === '';
  const swatchColor = isUnset ? defaultColor : normalizeHex(rawColor) || defaultColor;
  return (
    <div className={controlStyles.row}>
      <Text val={window.i18next.t(labelKey)} size={TextSize.MD} className={controlStyles.label} />
      <div className={styles.controls}>
        {hideClearButton ? null : renderClearButton({component, property, clearAriaKey})}
        {renderSwatchDropdown(component, property, swatchColor, isUnset, pickAriaKey)}
      </div>
    </div>
  );
}

function renderClearButton({component, property, clearAriaKey}: ClearButtonRenderArgs): ReactElement {
  return (
    <Icon
      icon="icon-delete"
      shape={IconShape.SQUARE}
      type={IconType.SECONDARY}
      size={IconSize.SIZE_ICON_16}
      ariaLabel={window.i18next.t(clearAriaKey)}
      onClick={(): void => {
        clearComponentStyleProperty(component, property);
      }}
    />
  );
}

function renderSwatchDropdown(component: Component, property: ColorControlProperty, currentColor: string, isUnset: boolean, pickAriaKey: string): ReactElement {
  const onChange = (val: string): void => {
    setComponentStyleProperty(component, property, val);
  };
  return (
    <Dropdown
      className={styles.swatchDropdown}
      selector={
        <button
          type="button"
          className={`${styles.swatch} ${isUnset ? styles.swatchEmpty : ''}`}
          style={isUnset ? undefined : {backgroundColor: currentColor}}
          aria-label={window.i18next.t(pickAriaKey)}
        >
          {renderEmptySwatchOverlay(isUnset)}
        </button>
      }
      popup={<ColorPickerPopup color={currentColor} onChange={onChange} onChangeEnded={onChange} isAlphaDisabled />}
      popupClassName={styles.pickerPopup}
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      position={DROPDOWN_POSITION.FIXED}
      doCloseOnInnerClick={false}
      popUpHasCustomWidth
      usePortal
    />
  );
}

function renderEmptySwatchOverlay(isUnset: boolean): ReactElement | null {
  if (!isUnset) {
    return null;
  }
  return <Icon icon="icon-plus" size={IconSize.SIZE_ICON_16} type={IconType.NONE} />;
}

function normalizeHex(raw: string): string {
  if (!raw) {
    return '';
  }
  return colorToHexString(raw);
}
